/* eslint-disable import/no-extraneous-dependencies */
import { lazy, useEffect, useState } from "react";

import {
  formatDataForGraph,
  getCurrentYearData,
  getTrendCardData,
  MIN_MANDATORY_MONTHS,
} from "./data";
import { TrendDataProps, TrendProps } from "./types";
import { shallowEqual, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import classes from "./PriceTrend.module.scss";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import PriceTrendFilter from "./components/PriceTrendFilter";
import { PAGE } from "constants/configConstants";
import TrendCard from "./components/TrendCard";
import { getPriceTrends } from "./helpers";
import SectionContainer from "../SectionContainer";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import LazyLoadOnScroll from "@Components/LazyLoadOnScroll";
//

const PriceTrendChart = lazy(() => import("./PriceTrendChart"));

const getInitialData = (
  priceTrendsData: any,
  duration: string | undefined,
): {
  trendData: TrendDataProps;
  currentYearData: TrendProps;
  isVisible: boolean;
} => {
  const defaultTrendData: TrendDataProps = { lastYear: [], currentYear: [] };
  const defaultCurrentYearData: TrendProps = {
    month_of_date: 0,
    year_of_date: 0,
    price: 0,
    cost_difference_percent: "",
    forecasting_percent_last_year: "",
    price_increase_last_year: "",
    total_savings_for_next_month: "",
    weekly_savings_next_month: "",
    yearly_savings_starting_next_month: "",
    city_avg_cost: "",
  };

  if (!priceTrendsData) {
    return {
      trendData: defaultTrendData,
      currentYearData: defaultCurrentYearData,
      isVisible: true,
    };
  }

  const trends = (priceTrendsData.trends || []).map((t: any) => ({ ...t }));
  if (trends.length >= MIN_MANDATORY_MONTHS) {
    const { currentYear, lastYear } = formatDataForGraph(trends, duration as string);
    return {
      trendData: { lastYear, currentYear },
      currentYearData: getCurrentYearData(trends, currentYear, lastYear, duration as string),
      isVisible: true,
    };
  }

  return { trendData: defaultTrendData, currentYearData: defaultCurrentYearData, isVisible: false };
};

const PriceTrend = ({ isDesktop = false }) => {
  const { id, pricing, region, children, priceTrendsData } = useSelector(
    ({ propertyPageData }: AppState) => ({
      id: propertyPageData.data?.id,
      pricing: propertyPageData.data?.pricing,
      region: propertyPageData.data?.region,
      children: propertyPageData.data?.children_short,
      priceTrendsData: propertyPageData.priceTrendsData,
    }),
    shallowEqual,
  );

  const initialData = getInitialData(priceTrendsData, pricing?.duration);

  const [trendData, setTrendData] = useState<TrendDataProps>(initialData.trendData);
  const [isVisible, setIsVisible] = useState(initialData.isVisible);
  const [currentYearData, setCurrentYearData] = useState<TrendProps>(initialData.currentYearData);
  const [selectedConfig, setSelectedConfig] = useState<number>(id);
  const [hasInitialized, setHasInitialized] = useState(!!priceTrendsData);
  const { t } = useTranslationHook();

  const trendCardsData = getTrendCardData(currentYearData, region, pricing, id, selectedConfig, t);

  const handleReset = () => {
    setSelectedConfig(id);
    getPriceTrendsData(id);
    gtagEventEmitter({
      action: "price_room_reset_clicked",
    });
  };

  const handleFilterChange = (val: number) => {
    setSelectedConfig(val);
    getPriceTrendsData(val);
    gtagEventEmitter({
      action: "Price_Room_changed",
      category: PAGE.INVENTORY,
      label: `Price Trend Room Changed`,
    });
    //  for desktop to close the dropdown
    document.getElementById("roomTypeDesktopBtn")?.click();
  };

  const getPriceTrendsData = async (selectedId: any) => {
    const res = await getPriceTrends(selectedId);
    if (res) {
      const data = res.data || {};
      const trends = data.trends || [];
      if (trends.length >= MIN_MANDATORY_MONTHS) {
        const { currentYear, lastYear } = formatDataForGraph(trends, pricing?.duration);
        setTrendData({
          lastYear,
          currentYear,
        });
        setCurrentYearData(getCurrentYearData(trends, currentYear, lastYear, pricing?.duration));
      } else if (selectedId === id) {
        setIsVisible(false);
      } else {
        setTrendData({
          lastYear: [],
          currentYear: [],
        });
        setCurrentYearData(getCurrentYearData([], [], [], pricing?.duration));
      }
    }
  };

  useEffect(() => {
    // Only fetch client-side if we don't have SSR data
    if (!hasInitialized) {
      getPriceTrendsData(id);
    }
    setHasInitialized(true);
  }, []);

  useEffect(() => {
    if (!isVisible) {
      const event = new Event("navigation_section_removed");
      window.dispatchEvent(event);
    }
  }, [isVisible]);

  if (!isVisible) {
    return <></>;
  }

  return (
    <>
      <SectionContainer
        dataTestId="Price Trend"
        title={t("propertypage.pricetrend.pricetrendtitle")}
        className={formatClassNames(classes.container, isDesktop && classes.desktop)}
        id="price_trend"
        titleClassName={classes.titleContainer}
        isPriceTrendSection
        headingLevel={2}
      >
        <PriceTrendFilter
          selectedConfig={+selectedConfig}
          isSelected={selectedConfig !== id}
          roomTypes={children?.reduce((roomTypes: any, room: any) => {
            roomTypes = [
              ...roomTypes,
              {
                value: room?.id,
                name: room?.name,
              },
            ];
            return roomTypes;
          }, [])}
          handleReset={handleReset}
          handleFilterChange={(val: number) => handleFilterChange(val)}
          isDesktop={isDesktop}
        />

        <div className={classes.trendCardsContainer}>
          {trendCardsData.map((trend) => (
            <TrendCard key={trend?.id} trend={trend} />
          ))}
        </div>

        <LazyLoadOnScroll
          fallback={
            <div
              className={formatClassNames(classes.chartBox, isDesktop && classes.chartBoxDesktop)}
              style={{ height: "280px" }}
            />
          }
        >
          <PriceTrendChart
            trendData={trendData}
            currentYearData={currentYearData}
            pricing={pricing}
            region={region}
            isDesktop={isDesktop}
          />
        </LazyLoadOnScroll>
      </SectionContainer>
    </>
  );
};

export default wrapperHOC(PriceTrend, {
  componentName: "PriceTrend-PropertyPage",
  showForChina: true,
  showForMiniProgram: false,
  showForWhiteLable: true,
});
